import type { TFunction } from 'i18next';
import queryString from 'query-string';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import TagManager from 'react-gtm-module';
import { useTranslation } from 'react-i18next';
import { Redirect, useHistory } from 'react-router-dom';
import removeTrailingSlash from 'remove-trailing-slash';

import { getFallbackAnalyticsInstanceId } from '~/common/analyticsInstances';
import {
  getInvitationMetadata,
  getRealmInfo,
  invitationSignup,
  signUp,
  type InvitationMetadata,
  type RealmInfo,
  type SignedUpUser
} from '~/common/api-client';
import {
  PRODUCT_URLS,
  ProductSlugs,
  getProductUrl,
  isKeyOfProductUrls,
  originalRedirectQueryParam,
  pendingPostAuthRedirectStorageKey
} from '~/common/constants';
import { useAnalyticsInstance } from '~/common/contexts/analytics';
import { useAuthContext } from '~/common/contexts/auth';
import { useEnterprises } from '~/common/contexts/enterprises';
import { useFeatureFlagState } from '~/common/contexts/featureFlags';
import useMediaQuery from '~/common/hooks/useMediaQuery';
import {
  getControllingSubscriptions,
  getViewerProductAccess
} from '~/common/utils/subscriptions';
import {
  isSupportRedirect,
  isValidRedirectURI
} from '~/common/utils/validate-redirect';
import validate, { type Errors } from '~/common/utils/validate-user-form';
import SignUpDetails from '../components/SignUpDetails';
import SignUpForm from '../components/SignUpForm';
import getProductFromUrl from '../utils/get-product-from-url';
import styles from './SignUp.css';

export type IDP = 'google' | 'github';

const mapSignupErrorMessageForUser = (
  errorMessage: string,
  t: TFunction,
  invalidEmailDisplay?: string
): string => {
  if (errorMessage.includes('competitor')) {
    return t('Account could not be created.');
  }
  if (errorMessage.includes('disposable')) {
    return t(
      'We do not allow signups from disposable email accounts, please use a real email account to sign up.'
    );
  }
  if (errorMessage.includes('Invalid email')) {
    if (invalidEmailDisplay) {
      return t('The provided email “{{ email }}” is not valid.', {
        email: invalidEmailDisplay
      });
    }
    return t('The provided email is not valid.');
  }
  if (
    errorMessage.toLowerCase().includes('user exists') ||
    errorMessage.toLowerCase().includes('already exists') ||
    errorMessage.toLowerCase().includes('username')
  ) {
    return t(
      'An account with this email may already exist. Try signing in, or use password recovery if you forgot your password.'
    );
  }
  return errorMessage;
};

const SignUp = () => {
  const { t } = useTranslation();
  const narrow = useMediaQuery('(max-width: 53.125rem)');
  const mobileViewPort = useMediaQuery('(max-width: 28.75rem)');
  const { billingUser, login, authenticateWithTokens } = useAuthContext();
  const {
    activeEnterprise,
    isAdmin,
    loading: enterprisesLoading
  } = useEnterprises();
  const history = useHistory();
  const {
    product: productParam,
    redirect_uri = '',
    metadata: metadataParam
  } = queryString.parse(history.location.search, {
    // This is the default, but we're being explicit here.
    decode: true
  });
  const isSupportRegistrationAllowed = useFeatureFlagState(
    'support_registration'
  );

  const rawProductSlug =
    productParam ||
    getProductFromUrl(typeof redirect_uri === 'string' ? redirect_uri : '');
  const productSlug: ProductSlugs | undefined = (
    Object.values(ProductSlugs) as ProductSlugs[]
  ).find(slug => slug === rawProductSlug);
  const isMcpSignup = productSlug === ProductSlugs.axeMCPServer;
  const productAnalyticsId = getFallbackAnalyticsInstanceId(productSlug);
  const productUrlsKey = isKeyOfProductUrls(productSlug) && productSlug;
  const analytics = useAnalyticsInstance(productAnalyticsId);

  const [submitted, setSubmitted] = useState(false);
  const [newUser, setNewUser] = useState<SignedUpUser>();
  const [submitLoading, setSubmitLoading] = useState(false);
  const [redirectLoading, setRedirectLoading] = useState(true);
  const [realmInfo, setRealmInfo] = useState<RealmInfo>();
  const [realmInfoReady, setRealmInfoReady] = useState(false);
  const [errors, setErrors] = useState<Errors>({});
  const [redirectURI, setRedirectURI] = useState<string>('');
  const [invitationData, setInvitationData] =
    useState<InvitationMetadata | null>(null);
  const [invitationCompanyName, setInvitationCompanyName] = useState('');
  const [invitationMetadataReady, setInvitationMetadataReady] = useState(
    typeof metadataParam !== 'string' || !metadataParam
  );
  const focusTarget = useRef<HTMLDivElement>(null);
  const firstName = useRef<HTMLInputElement>(null);
  const lastName = useRef<HTMLInputElement>(null);
  const email = useRef<HTMLInputElement>(null);
  const company = useRef<HTMLInputElement>(null);
  const password = useRef<HTMLInputElement>(null);
  const confirmPassword = useRef<HTMLInputElement>(null);
  const termsAccepted = useRef<HTMLInputElement>(null);
  const consentAccepted = useRef<HTMLInputElement>(null);

  const metadata =
    typeof metadataParam === 'string' ? metadataParam : undefined;
  const invitationMode = !!invitationData;

  const { isMobileReadOnly, isMobileFreeTrial } = useMemo(() => {
    return {
      isMobileReadOnly: productSlug === ProductSlugs.axeDevToolsMobile,
      isMobileFreeTrial: redirectURI.includes(
        '/axe-devtools-mobile/get-started'
      )
    };
  }, [productSlug, redirectURI]);

  useEffect(() => {
    if (typeof redirect_uri === 'string' && redirect_uri !== '') {
      setRedirectURI(redirect_uri);
      return;
    }
    const defaultRedirectURI = isMcpSignup
      ? `${window.location.origin}${PRODUCT_URLS[ProductSlugs.axeMCPServer].gettingStarted}`
      : window.location.origin;
    setRedirectURI(defaultRedirectURI);

    if (isMcpSignup) {
      sessionStorage.setItem(
        pendingPostAuthRedirectStorageKey,
        defaultRedirectURI
      );
    }
  }, [redirect_uri, isMcpSignup]);

  useEffect(() => {
    const getData = async () => {
      try {
        const info = await getRealmInfo();
        setRealmInfo(info);
      } catch {
        setErrors({
          alert: t('We were unable to fetch password requirements')
        });
      } finally {
        setRealmInfoReady(true);
      }
    };

    getData();
  }, []);

  useEffect(() => {
    if (!metadata) {
      setInvitationData(null);
      setInvitationCompanyName('');
      setInvitationMetadataReady(true);
      return;
    }

    setInvitationMetadataReady(false);

    const fetchMetadata = async () => {
      try {
        const data = await getInvitationMetadata(metadata);
        setInvitationData(data);
        setInvitationCompanyName(data.enterpriseName);
        setErrors(previousErrors => ({
          ...previousErrors,
          alert: ''
        }));
      } catch {
        setInvitationData(null);
        setInvitationCompanyName('');
        setErrors({
          alert: t(
            'Invalid or expired invitation link. Please contact your administrator for a new invitation.'
          )
        });
      } finally {
        setInvitationMetadataReady(true);
      }
    };

    fetchMetadata();
  }, [metadata, t]);

  useEffect(() => {
    analytics.viewSignUp({ href: window.location.href });
  }, []);

  useEffect(() => {
    if (!focusTarget.current || !submitted) {
      return;
    }

    // focus the post-form-submit loader / focus the post-loading success message
    focusTarget.current.focus();
  }, [submitted, submitLoading]);

  useEffect(() => {
    if (enterprisesLoading) {
      return;
    }

    // redirect to welcome page if user has paid or trialing access, otherwise redirect to plans page
    // If redirect page is undefined, navigate to "/"
    if (billingUser && productUrlsKey) {
      const productAccess = getViewerProductAccess(
        billingUser,
        activeEnterprise,
        isAdmin
      );
      const hasPaidOrTrialingAccess = ['paid', 'trialing'].includes(
        productAccess[productUrlsKey]
      );
      const signedInUserRedirect = hasPaidOrTrialingAccess
        ? getProductUrl(productUrlsKey, 'welcome')
        : getProductUrl(productUrlsKey, 'plans');

      if (
        signedInUserRedirect &&
        (!activeEnterprise ||
          getControllingSubscriptions(billingUser, activeEnterprise.id).some(
            ({ subscription }) => subscription.product_slug === productUrlsKey
          ))
      ) {
        setRedirectURI(signedInUserRedirect);
      }
    } else if (redirect_uri && typeof redirect_uri === 'string') {
      if (isValidRedirectURI(redirect_uri)) {
        // redirect to `redirect_uri` if it is valid
        setRedirectURI(redirect_uri);
      }
      if (isSupportRedirect(redirect_uri) && isSupportRegistrationAllowed) {
        // override redirect uri if it is support domain with new param
        setRedirectURI(
          `${
            window.location.origin
          }?${originalRedirectQueryParam}=${encodeURIComponent(redirect_uri)}`
        );
      }
    }
    setRedirectLoading(false);
  }, [enterprisesLoading, billingUser, redirect_uri, productUrlsKey]);

  const onSubmitInvitation = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (
      !firstName.current ||
      !lastName.current ||
      !password.current ||
      !confirmPassword.current ||
      !invitationData ||
      !metadata
    ) {
      setErrors({ alert: t('Something went wrong. Please try again.') });
      return;
    }

    const companyForValidate = {
      value: invitationCompanyName
    } as HTMLInputElement;

    const newErrors = validate(
      {
        firstName: firstName.current,
        lastName: lastName.current,
        email: { value: invitationData.email } as HTMLInputElement,
        company: companyForValidate,
        password: password.current,
        confirmPassword: confirmPassword.current
      },
      realmInfo?.passwordPolicy || {},
      t
    );

    if (!termsAccepted.current?.checked) {
      newErrors.termsAccepted = t(
        'You must accept the terms and conditions to continue'
      );
    }

    setErrors(newErrors);
    if (Object.keys(newErrors).length) {
      return;
    }

    setSubmitLoading(true);
    setSubmitted(true);
    try {
      const result = await invitationSignup({
        firstName: firstName.current.value,
        lastName: lastName.current.value,
        company: invitationCompanyName,
        email: invitationData.email,
        password: password.current.value,
        passwordConfirmation: confirmPassword.current.value,
        metadata,
        termsAccepted: true
      });

      TagManager.dataLayer({ dataLayer: { event: 'axeSignup' } });

      try {
        await analytics.identify({ user: result.user });
        await analytics.signUp();
      } catch {
        // fail silently
      }

      const fallbackToManualSignIn = () => {
        setSubmitted(false);
        setErrors({
          alert: t(
            'Your account was created, but automatic sign-in did not finish. Please sign in with the password you chose.'
          )
        });
      };

      if (!result.tokens || result.signInRequired) {
        fallbackToManualSignIn();
      } else {
        try {
          await authenticateWithTokens(result.tokens);
        } catch {
          fallbackToManualSignIn();
          return;
        }
        history.push(
          `/accept-invitation?invitation_id=${encodeURIComponent(invitationData.invitationId)}&enterprise_id=${encodeURIComponent(invitationData.enterpriseId)}`
        );
      }
    } catch (error) {
      const errorMessage: string = (error as Error).message;
      setErrors({
        alert: mapSignupErrorMessageForUser(errorMessage, t)
      });
    } finally {
      setSubmitLoading(false);
    }
  };

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    // Ensure we have all of our input refs
    /* istanbul ignore if */
    if (
      !firstName.current ||
      !lastName.current ||
      !email.current ||
      !company.current ||
      !password.current ||
      !confirmPassword.current
    ) {
      setErrors({
        alert: t('Something went wrong. Please try again.')
      });
      return;
    }

    const newErrors = validate(
      {
        firstName: firstName.current,
        lastName: lastName.current,
        email: email.current,
        company: company.current,
        password: password.current,
        confirmPassword: confirmPassword.current
      },
      realmInfo?.passwordPolicy || {},
      t
    );

    setErrors(newErrors);
    if (Object.keys(newErrors).length) {
      // focus the first erroneous input
      return;
    }

    setSubmitLoading(true);
    setSubmitted(true);
    try {
      const signupResult = await signUp({
        isMobileReadOnly,
        isMobileFreeTrial,
        redirectURI,
        // typescript doesn't follow the codepath of setErrors so it
        // doesn't know that these refs are guaranteed to have a value
        firstName: firstName.current?.value,
        lastName: lastName.current?.value,
        company: company.current?.value,
        email: email.current?.value,
        password: password.current?.value,
        passwordConfirmation: confirmPassword.current?.value,
        marketingConsent: isMcpSignup
          ? !!consentAccepted.current?.checked
          : undefined,
        product_slug: productSlug
      });
      setNewUser(signupResult);
      TagManager.dataLayer({
        dataLayer: {
          event: 'axeSignup'
        }
      });
      try {
        await analytics.identify({ user: signupResult });
        await analytics.signUp();
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error); // we just log and ignore the error here and not intimate/block the user about analytics error
      }
    } catch (error) {
      const errorMessage: string = (error as Error).message;
      setErrors({
        alert: mapSignupErrorMessageForUser(
          errorMessage,
          t,
          email.current?.value
        )
      });
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleLogin = (idp?: IDP) => {
    const url = new URL(redirectURI);
    if (idp) {
      url.searchParams.set('socialProvider', idp);
    }
    login(removeTrailingSlash(url.toString()));
  };

  const pageDataLoading =
    !realmInfoReady || !invitationMetadataReady || submitLoading;

  if (billingUser && !enterprisesLoading && !redirectLoading) {
    const { origin, pathname, search } = new URL(
      redirectURI,
      window.location.origin
    );
    if (origin !== window.location.origin) {
      window.location.href = redirectURI;
      return null;
    }
    return <Redirect to={`${pathname}${search}`} />;
  }

  return (
    <div className={styles.wrapper}>
      <SignUpDetails
        isNarrow={narrow}
        isMobileViewPort={mobileViewPort}
        productSlug={productAnalyticsId}
      />
      <SignUpForm
        loading={pageDataLoading || (!!billingUser && enterprisesLoading)}
        onSubmit={invitationMode ? onSubmitInvitation : onSubmit}
        errors={errors}
        realmInfo={realmInfo}
        firstName={firstName}
        lastName={lastName}
        email={email}
        company={company}
        password={password}
        confirmPassword={confirmPassword}
        newUser={newUser}
        focusTarget={focusTarget}
        handleLogin={handleLogin}
        product={productAnalyticsId}
        invitationMode={invitationMode}
        prefillEmail={invitationData?.email}
        enterpriseName={invitationData?.enterpriseName}
        invitationCompanyName={
          invitationMode ? invitationCompanyName : undefined
        }
        termsAccepted={termsAccepted}
        consentAccepted={isMcpSignup ? consentAccepted : undefined}
      />
    </div>
  );
};

export default SignUp;
